/** @file Hook: drag-scrub of a number over a handle element with pointer capture; shift = fine. */
import { useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import { advanceScrub, type ScrubSession } from './logic/advanceScrub';
import { constrainNumber, type NumberBounds } from './logic/constrainNumber';

/** Inputs of useScrub. */
export interface ScrubOptions {
  value: number;
  step: number;
  bounds: NumberBounds;
  disabled: boolean;
  /** Called with every rounded, clamped value during the drag (local preview, no command yet). */
  onPreview?: ((value: number) => void) | undefined;
  /** Called once on release with the final value, only when it differs from `value`. */
  onCommit: (value: number) => void;
}

/** Live preview (null when idle) and the pointer handlers to spread on the handle element. */
export interface ScrubHandle {
  preview: number | null;
  handlers: {
    onPointerDown: (e: PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: PointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: PointerEvent<HTMLElement>) => void;
    onClick: (e: MouseEvent<HTMLElement>) => void;
  };
}

/** Scrub state machine: down captures the pointer, move previews, up commits once, cancel restores the old value. */
export function useScrub(o: ScrubOptions): ScrubHandle {
  const session = useRef<ScrubSession | null>(null);
  const suppressClick = useRef(false);
  const [preview, setPreview] = useState<number | null>(null);
  const end = (e: PointerEvent<HTMLElement>, commit: boolean): void => {
    const s = session.current;
    if (!s) return;
    session.current = null;
    setPreview(null);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    suppressClick.current = s.moved;
    const final = constrainNumber(s.raw, o.bounds);
    if (commit && s.moved && final !== o.value) o.onCommit(final);
    else if (s.moved) o.onPreview?.(o.value);
  };
  return {
    preview,
    handlers: {
      onPointerDown: (e) => {
        if (o.disabled || e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        session.current = { raw: o.value, lastX: e.clientX, moved: false };
        suppressClick.current = false;
      },
      onPointerMove: (e) => {
        const before = session.current;
        if (!before) return;
        const after = advanceScrub(before, e.clientX, e.shiftKey, o.step, o.bounds);
        session.current = after;
        if (after === before) return;
        const shown = constrainNumber(after.raw, o.bounds);
        setPreview(shown);
        o.onPreview?.(shown);
      },
      onPointerUp: (e) => { end(e, true); },
      onPointerCancel: (e) => { end(e, false); },
      onClick: (e) => {
        if (suppressClick.current) e.preventDefault();
        suppressClick.current = false;
      },
    },
  };
}
